import handleError from '@fuse/core/errorHandler';
import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { API } from 'app/configs/api';

import axios from 'axios';

export const getChats = createAsyncThunk(
  'chatPanel/chats/getChats',
  async (params, { dispatch, getState }) => {
    const userId = getState().user?.id || '';
    try {
      const { data = [] } = await axios.get(`${API.chats}`);

      const chats = data?.map((chat) => {
        if (chat.type === 0) {
          const member = chat.members.find((m) => m.id !== userId);

          chat.fullName = member ? member.fullName : 'Saved Messages';
          chat.contactId = member ? member.id : userId;
        }

        return chat;
      });

      return chats;
    } catch (e) {
      handleError(e);
      return [];
    }
  }
);

export const getChatsIfNotExist = createAsyncThunk(
  'chatPanel/chats/getChatsIfNotExist',
  async (params, { dispatch, getState }) => {
    const isExist = getState()?.chatPanel?.chats?.ids?.includes(params?.chatGroupId);
    if (isExist) {
      return state?.chatPanel?.chats;
    } else {
      const userId = getState().user?.id || '';
      try {
        const { data } = await axios.get(`${API.chats}`);
        const chats = data?.map((chat) => {
          if (chat.type === 0) {
            const member = chat.members.find((m) => m.id !== userId);
            chat.fullName = member ? member.fullName : 'Saved Messages';
            chat.contactId = member ? member.id : userId;
          }
          return chat;
        });
        return chats;
      } catch (e) {
        handleError(e);
        return [];
      }
    }
  }
);

const chatsAdapter = createEntityAdapter({});

export const { selectAll: selectChats, selectById: selectChatById } = chatsAdapter.getSelectors(
  (state) => state.chatPanel.chats
);

const chatsSlice = createSlice({
  name: 'chatPanel/chats',
  initialState: chatsAdapter.getInitialState({
    isInitialized: true,
  }),
  reducers: {
    setChatPhoto: (state, { payload: { id, photoUrl } }) => {
      state.ids.forEach((key) => {
        const member =
          state.entities[key].type === 0 &&
          state.entities[key].members.find((item) => item.id === id);
        if (member) {
          state.entities[key].photoURL = photoUrl;

          if (state.entities[key].type === 0) {
            state.entities[key].fullName = member ? member.fullName : 'Saved Messages';
            state.entities[key].contactId = member?.id;
          }
        }
      });
    },
    addUnreadMessage: (state, { payload: message }) => {
      state.entities[message.chatGroupId].unreadMessagesCount += 1;
      state.entities[message.chatGroupId].lastMessageText = message.text;
      state.entities[message.chatGroupId].lastMessageDate = message.createdDate;
    },
    substractUnreadMessage: (state, { payload: message }) => {
      if (state.entities[message.chatGroupId]) {
        if (state.entities[message.chatGroupId].unreadMessagesCount > 0) {
          state.entities[message.chatGroupId].unreadMessagesCount -= 1;
        }
      }
    },
    addLastMessage: (state, { payload: message }) => {
      if (state.entities[message.chatGroupId]) {
        state.entities[message.chatGroupId].lastMessageText = message.text;
        state.entities[message.chatGroupId].lastMessageDate = message.createdDate;
        state.entities[message.chatGroupId].lastMessageId = message.id;
      }
    },
    editLastMessage: (state, { payload: message }) => {
      if (state.entities[message.chatGroupId]) {
        const isLast = state.entities[message.chatGroupId].lastMessageId === message.id;
        if (isLast) {
          state.entities[message.chatGroupId].lastMessageText = message.text;
          state.entities[message.chatGroupId].lastMessageId = message.id;
        }
      }
    },
    initializeChat: (state) => {
      if (state?.isInitialized) {
        state.isInitialized = true;
      }
    },
    readUnreadMessages: (state, { payload: chatId }) => {
      if (state.entities[chatId]) {
        state.entities[chatId].unreadMessagesCount = 0;
      }
    },
    addNewMember: (state, { payload: { chatId, newMembers } }) => {
      if (state.entities[chatId]) {
        state.entities[chatId].members = [...state.entities[chatId].members, ...newMembers];
      }
    },
    removeMemberFromGroup: (state, { payload: { userId, chatId } }) => {
      const memberIndex = state.entities[chatId].members.findIndex((m) => m.id === userId);
      if (memberIndex > -1) {
        state.entities[chatId].members = [
          ...state.entities[chatId].members.slice(0, memberIndex),
          ...state.entities[chatId].members.slice(memberIndex + 1),
        ];
      }
    },
    removeGroup: (state, { payload }) => {
      chatsAdapter.removeOne(state, payload);
    },
    leaveChat: (state, { payload: { chatId } }) => {
      chatsAdapter.removeOne(state, chatId);
    },
  },
  extraReducers: {
    [getChats.fulfilled]: chatsAdapter.setAll,
    [getChatsIfNotExist.fulfilled]: chatsAdapter.setAll,
  },
});

export const selectIsInitialized = ({ chatPanel }) => chatPanel.chats.isInitialized;

export const {
  setChatPhoto,
  addUnreadMessage,
  substractUnreadMessage,
  readUnreadMessages,
  addLastMessage,
  editLastMessage,
  addNewMember,
  removeMemberFromGroup,
  initializeChat,
  removeGroup,
  leaveChat,
} = chatsSlice.actions;

export default chatsSlice.reducer;
